import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import Swal from 'sweetalert2';
import withPermission from '../../utils/withPermission';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/amc-settings`;

const AMCSettings = () => {
    const [amcList, setAmcList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        amc_type: "",
        percentage: "",
        min_amount: "",
        status: "active"
    });

    const [isEdit, setIsEdit] = useState(false);
    const [selectedID, setSelectedID] = useState(null);
    const [errors, setErrors] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [pageCount, setPageCount] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const debounceTimeoutRef = useRef(null);

    const WithCreatePermission = withPermission(({ children }) => children, ['amc-create']);
    const WithEditPermission = withPermission(({ children }) => children, ['amc-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['amc-delete']);

    // ✅ Fetch AMC list
    useEffect(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

        debounceTimeoutRef.current = setTimeout(() => {
            fetchAMC(1);
        }, searchQuery ? 800 : 0);

        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, [searchQuery]);

    const fetchAMC = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");

            const res = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${userInfo?.token}`,
                    "Tenant-Origin": origin,
                },
                params: { page, search: searchQuery }
            });

            setAmcList(res.data.data);
            setPageCount(res.data.last_page || 1);
            setCurrentPage(res.data.current_page || 1);
        } catch (err) {
            console.error("Error fetching AMC settings:", err);
        }
    };

    // ✅ Open Add Modal
    const handleAdd = () => {
        setFormData({
            amc_type: "",
            percentage: "",
            min_amount: "",
            status: "active"
        });
        setIsEdit(false);
        setShowModal(true);
        setErrors({});
    };

    // ✅ Edit
    const handleEdit = (item) => {
        setFormData(item);
        setSelectedID(item.id);
        setIsEdit(true);
        setShowModal(true);
        setErrors({});
    };

    // ✅ Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    // ✅ Validate
    const validateForm = () => {
        const newErrors = {};

        if (!formData.amc_type) newErrors.amc_type = "AMC Type is required";
        if (!formData.percentage) newErrors.percentage = "Percentage required";

        return newErrors;
    };
 const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };
    // ✅ Submit
    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");

            if (isEdit) {
                await axios.put(`${API_URL}/${selectedID}`, formData, {
                    headers: {
                        Authorization: `Bearer ${userInfo?.token}`,
                        "Tenant-Origin": origin,
                    },
                });
            } else {
                await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${userInfo?.token}`,
                        "Tenant-Origin": origin,
                    },
                });
            }

            setShowModal(false);
            fetchAMC(currentPage);
        } catch (err) {
            console.error("Error saving AMC:", err);
        }
    };

    // ✅ Delete
    const handleDelete = (id) => {
        Swal.fire({
            title: "Confirm Delete?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem("user-info"));
                    const origin = localStorage.getItem("user-origin");

                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${userInfo?.token}`,
                            "Tenant-Origin": origin,
                        },
                    });

                    fetchAMC(currentPage);
                    Swal.fire("Deleted!", "AMC setting removed.", "success");
                } catch (err) {
                    Swal.fire("Error!", "Failed to delete.", "error");
                }
            }
        });
    };

    const handlePageChange = ({ selected }) => {
        const page = selected + 1;
        setCurrentPage(page);
        fetchAMC(page);
    };

    // ✅ UI Layout
    return (
         <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Brand</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        {/* <li className="breadcrumb-item"><a href="javascript:void(0);">Brand</a></li> */}
                                        {/* <li className="breadcrumb-item active">Brand List</li> */}
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>



                                        </div>

                                        <WithCreatePermission>
                                        <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                  
                                                    onClick={handleAdd}                                                     data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Add Brand"
                                                >
                                                    <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                                                </button>
                                            </WithCreatePermission>
                                    </div>
                                    <table className="table align-middle table-nowrap table-check table-list">

                                        <thead className="table-sales-gradient">
                            <tr>
                                <th>#</th>
                                <th>AMC Type</th>
                                <th>Percentage (%)</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {amcList.length > 0 ? (
                                amcList.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>{item.amc_type}</td>
                                        <td>{item.percentage}</td>
                                        <td>
                                            {item.status === "active" ? (
                                                <span className="badge bg-success">Active</span>
                                            ) : (
                                                <span className="badge bg-danger">Inactive</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="dropdown">
                                                <i
                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                    data-bs-toggle="dropdown"
                                                    style={{ cursor: "pointer" }}
                                                ></i>
                                                <ul className="dropdown-menu">
                                                    <WithEditPermission>
                                                        <li>
                                                            <button className="dropdown-item" onClick={() => handleEdit(item)}>Edit</button>
                                                        </li>
                                                    </WithEditPermission>

                                                    <WithDeletePermission>
                                                        <li>
                                                            <button className="dropdown-item" onClick={() => handleDelete(item.id)}>Delete</button>
                                                        </li>
                                                    </WithDeletePermission>
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">No data found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    <ReactPaginate
                        previousLabel={'<'}
                        nextLabel={'>'}
                        pageCount={pageCount}
                        onPageChange={handlePageChange}
                        containerClassName={'pagination justify-content-end'}
                        activeClassName={'active'}
                        pageClassName={'page-item'}
                        pageLinkClassName={'page-link'}
                        previousClassName={'page-item'}
                        nextClassName={'page-item'}
                        previousLinkClassName={'page-link'}
                        nextLinkClassName={'page-link'}
                    />

                    {/* Modal (Offcanvas) */}
                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement="end" style={{ width: "420px" }}>
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>{isEdit ? "Edit AMC Setting" : "Add AMC Setting"}</Offcanvas.Title>
                        </Offcanvas.Header>

                        <Offcanvas.Body>
                            <Form>

                                {/* AMC Type */}
                                <Form.Group className="mb-3">
                                    <Form.Label>AMC Type</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="amc_type"
                                        value={formData.amc_type}
                                        onChange={handleInputChange}
                                        className={errors.amc_type ? "is-invalid" : ""}
                                    />
                                    {errors.amc_type && <div className="invalid-feedback">{errors.amc_type}</div>}
                                </Form.Group>

                                {/* Percentage */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Percentage (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="percentage"
                                        value={formData.percentage}
                                        onChange={handleInputChange}
                                        className={errors.percentage ? "is-invalid" : ""}
                                    />
                                    {errors.percentage && <div className="invalid-feedback">{errors.percentage}</div>}
                                </Form.Group>

                                {/* Min Amount */}
                                {/* <Form.Group className="mb-3">
                                    <Form.Label>Minimum Amount</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="min_amount"
                                        value={formData.min_amount}
                                        onChange={handleInputChange}
                                        className={errors.min_amount ? "is-invalid" : ""}
                                    />
                                    {errors.min_amount && <div className="invalid-feedback">{errors.min_amount}</div>}
                                </Form.Group> */}

                                {/* Status */}
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <div>
                                        <label>
                                            <input
                                                type="radio"
                                                name="status"
                                                value="active"
                                                checked={formData.status === "active"}
                                                onChange={handleInputChange}
                                            /> Active
                                        </label>

                                        <label className="ms-3">
                                            <input
                                                type="radio"
                                                name="status"
                                                value="inactive"
                                                checked={formData.status === "inactive"}
                                                onChange={handleInputChange}
                                            /> Inactive
                                        </label>
                                    </div>
                                </Form.Group>

                            </Form>
                        </Offcanvas.Body>

                        <div className="p-3 border-top d-flex justify-content-end">
                            <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                            <Button variant="primary" onClick={handleSubmit}>{isEdit ? "Update" : "Save"}</Button>
                        </div>
                    </Offcanvas>
                </div>
            </div>
        </div>
        </div>
        </div>
        </div>
        </div>
    );
};

export default AMCSettings;
